import { Link } from 'react-router-dom';
import { Instagram, Facebook } from 'lucide-react';
import Wordmark from '../brand/Wordmark';

export const Footer = () => (
  <footer className="border-t border-pd bg-[color:var(--pd-bg)]" data-testid="site-footer">
    {/* Prominent free-entry notice — own row above standard footer, brass gold left border */}
    <div
      className="border-l-4 border-[color:var(--pd-gold)] bg-[color:var(--pd-bg-2)]"
      data-testid="footer-free-entry-row"
    >
      <div className="container-pd py-3 md:py-4">
        <p className="text-sm text-[color:var(--pd-text)]">
          <span className="font-display text-[color:var(--pd-gold)] tracking-wide mr-2">No purchase necessary</span>
          <span className="text-muted-sage">&mdash; free postal entry available on every competition. See <Link to="/terms" className="underline underline-offset-4 hover:text-[color:var(--pd-text)]">Terms &amp; Conditions</Link> for details.</span>
        </p>
      </div>
    </div>

    <div className="container-pd py-8 md:py-12 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
      <div className="col-span-2 md:col-span-2">
        <Wordmark />
        <p className="mt-3 md:mt-4 text-sm text-muted-sage max-w-md">
          Premium padel prizes. Every draw is decided by a cryptographically-seeded random number generator with a fully published audit trail, and additionally confirmed live by an independent witness. Fully compliant with UK prize competition law.
        </p>
        <p className="mt-3 text-[11px] font-mono uppercase tracking-[0.16em] text-[color:var(--pd-gold)]" data-testid="footer-motto">
          Equal Odds, Equal Opportunity.
        </p>
      </div>
      <div>
        <div className="font-mono text-xs text-muted-sage mb-3">Explore</div>
        <ul className="space-y-1.5 md:space-y-2 text-sm">
          <li><Link to="/competitions" className="hover:text-[color:var(--pd-accent-yellow)]">All competitions</Link></li>
          <li><Link to="/how-it-works" className="hover:text-[color:var(--pd-accent-yellow)]">How it works</Link></li>
          <li><Link to="/winners" className="hover:text-[color:var(--pd-accent-yellow)]">Winners</Link></li>
          <li><Link to="/past-draws" className="hover:text-[color:var(--pd-accent-yellow)]" data-testid="footer-past-draws">Past draws</Link></li>
          <li><Link to="/how-we-draw-winners" className="hover:text-[color:var(--pd-accent-yellow)]" data-testid="footer-how-we-draw">How we draw winners</Link></li>
          <li><Link to="/giving-back" className="hover:text-[color:var(--pd-accent-yellow)]" data-testid="footer-giving-back">Giving back</Link></li>
          <li><Link to="/about" className="hover:text-[color:var(--pd-accent-yellow)]">About</Link></li>
          <li><Link to="/faq" className="hover:text-[color:var(--pd-accent-yellow)]">FAQ</Link></li>
        </ul>
      </div>
      <div>
        <div className="font-mono text-xs text-muted-sage mb-3">Legal</div>
        <ul className="space-y-1.5 md:space-y-2 text-sm">
          <li><Link to="/terms" className="hover:text-[color:var(--pd-accent-yellow)]">Terms &amp; Conditions</Link></li>
          <li><Link to="/privacy" className="hover:text-[color:var(--pd-accent-yellow)]">Privacy Policy</Link></li>
          <li><Link to="/safer-play" className="hover:text-[color:var(--pd-accent-yellow)]" data-testid="footer-safer-play">Safer Play</Link></li>
          <li><Link to="/data-request" className="hover:text-[color:var(--pd-accent-yellow)]">Data deletion request</Link></li>
          <li><Link to="/contact" className="hover:text-[color:var(--pd-accent-yellow)]">Contact</Link></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-pd">
      <div className="container-pd py-4 md:py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
        <p className="text-xs text-muted-sage">
          The Padel Draw Ltd &middot; Registered in England &amp; Wales &middot; Company No. 17363280 &middot; Registered office: Mulberry House, Shipton Oliffe, Cheltenham, GL54 4HU
        </p>
        <div className="flex items-center gap-5" data-testid="footer-bottom-strip">
          {/* Card payment marks — sits alongside the socials, subtle, brand-accurate */}
          <div className="flex items-center gap-2" aria-label="Cards accepted" data-testid="footer-card-logos">
            <span className="sr-only">Visa, Mastercard and Apple Pay accepted</span>
            {/* Visa wordmark */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 16" className="h-5 w-auto" role="img" aria-label="Visa">
              <rect width="48" height="16" rx="2" fill="#ffffff" />
              <path fill="#1A1F71" d="M20.6 4.1l-2.3 7.8h-1.9l2.3-7.8h1.9zm7.7 5l1-2.7.6 2.7h-1.6zM30 11.9h1.7L30.3 4.1h-1.6c-.4 0-.7.2-.9.6l-2.7 7.2h2l.4-1.1h2.4l.1 1.1zM24.9 9.3c0-1.9-2.6-2-2.6-2.9 0-.2.3-.5 1-.6.7-.1 1.4.2 2 .5l.3-1.6a5 5 0 0 0-1.9-.4c-1.9 0-3.3 1-3.3 2.5 0 1.1.9 1.7 1.7 2 .8.4 1.1.6 1.1 1 0 .5-.6.7-1.2.7-1 0-1.6-.3-2-.5l-.4 1.6c.5.3 1.4.5 2.4.5 2 0 3.3-1 3.3-2.5v-.3zM16.3 4.1L13.1 12h-2l-1.6-6c-.1-.4-.2-.5-.5-.7-.5-.3-1.4-.5-2.1-.7l.1-.4h3.3c.4 0 .8.3.9.8l.8 4.4L14 4.1h2.2z"/>
            </svg>
            {/* Mastercard interlocking circles */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 32" className="h-5 w-auto" role="img" aria-label="Mastercard">
              <rect width="48" height="32" rx="2" fill="#ffffff" />
              <circle cx="19" cy="16" r="8" fill="#EB001B" />
              <circle cx="29" cy="16" r="8" fill="#F79E1B" />
              <path fill="#FF5F00" d="M24 10.2a8 8 0 0 1 0 11.6 8 8 0 0 1 0-11.6z"/>
            </svg>
            {/* Apple Pay wordmark — Apple's HIG-compliant black pill with white "Pay" and Apple glyph */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 32" className="h-5 w-auto" role="img" aria-label="Apple Pay">
              <rect width="48" height="32" rx="4" fill="#000000" />
              {/* Apple glyph, simplified */}
              <path fill="#ffffff" d="M14.9 12.4c.5-.7.9-1.6.8-2.5-.8 0-1.7.5-2.3 1.2-.5.6-.9 1.5-.8 2.4.9.1 1.8-.4 2.3-1.1zm.8.9c-1.3-.1-2.4.7-3 .7-.6 0-1.5-.7-2.5-.7-1.3 0-2.5.8-3.1 1.9-1.3 2.3-.3 5.7 1 7.6.6.9 1.4 2 2.4 1.9 1 0 1.3-.6 2.5-.6 1.1 0 1.4.6 2.5.6 1 0 1.7-.9 2.3-1.9.7-1 1-2.1 1-2.1 0 0-2-.8-2-3 0-1.9 1.5-2.7 1.6-2.8-.9-1.3-2.2-1.5-2.7-1.6z"/>
              {/* "Pay" wordmark */}
              <text x="21" y="21" fill="#ffffff" fontFamily="'Helvetica Neue', Helvetica, Arial, sans-serif" fontSize="10" fontWeight="700" letterSpacing="-0.3">Pay</text>
            </svg>
          </div>
          <a href="https://instagram.com/thepadeldraw" target="_blank" rel="noreferrer" className="text-muted-sage hover:text-[color:var(--pd-accent-yellow)]" aria-label="Instagram" data-testid="footer-instagram">
            <Instagram size={18} />
          </a>
          <a href="https://facebook.com/thepadeldraw" target="_blank" rel="noreferrer" className="text-muted-sage hover:text-[color:var(--pd-accent-yellow)]" aria-label="Facebook">
            <Facebook size={18} />
          </a>
        </div>
      </div>
      <div className="container-pd pb-5 md:pb-6 text-[11px] text-muted-sage">
        Competitions operated under UK prize competition law. Every draw is decided by a cryptographically-seeded random number generator whose seed and eligible ticket list are published, so anyone can recompute the result &mdash; and is additionally confirmed live by an independent witness. See our <Link to="/how-we-draw-winners" className="underline hover:text-[color:var(--pd-text)]">How we draw winners</Link> page for the operational detail.
      </div>
    </div>
  </footer>
);

export default Footer;
